/**
 * The smaller till dialogs: hold, recall, discount, note, customer, table and
 * the per-line options sheet.
 *
 * Grouped in one file because each is short and they share the same shape - a
 * modal, a little state, one action. Splitting them would be eight files of
 * imports for not much code.
 */

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    FiClock, FiSearch, FiUser, FiUserPlus, FiGrid, FiTrash2,
    FiPercent, FiCheck
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import Modal from '../ui/Modal';
import NumericKeypad from './NumericKeypad';
import { EmptyState, StatusBadge } from '../ui/Feedback';
import { SpinnerBlock } from '../ui/Spinner';
import { Field, Input } from '../ui/Field';
import { orderApi, customerApi, tableApi } from '../../services/endpoints';
import { selectCart, selectTotals, setOrderDiscount, setOrderNote, setLineDiscount, setLineNote, removeLine, setCustomer, setTable } from '../../store/cartSlice';
import { selectLookups } from '../../store/menuSlice';
import { money, relativeTime, duration } from '../../utils/format';

/* ================================== Hold ================================== */

/**
 * Park the current bill so the till is free for the next customer.
 * The label is what makes the Recall list usable - "Table 5 - Ahmed" rather
 * than a row of order numbers.
 */
export const HoldDialog = ({ open, onClose, onHold, busy }) => {
    const cart = useSelector(selectCart);
    const totals = useSelector(selectTotals);
    const [label, setLabel] = useState('');

    useEffect(() => {
        if (!open) return;
        // A sensible default so the cashier can just press Hold.
        setLabel(
            cart.tableNumber ? `Table ${cart.tableNumber}`
                : cart.customerName !== 'Walk-in Customer' ? cart.customerName
                    : ''
        );
    }, [open, cart.tableNumber, cart.customerName]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Hold this order"
            subtitle="It stays on the Recall list until it is paid or voided."
            width="sm"
            footer={(
                <>
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => onHold(label.trim())}
                        disabled={busy}
                    >
                        {busy ? 'Holding…' : 'Hold order'}
                    </button>
                </>
            )}
        >
            <Field label="Label" hint="Something you will recognise on the Recall list.">
                <Input
                    value={label}
                    onChange={(event) => setLabel(event.target.value)}
                    placeholder="Table 5 - Ahmed"
                    maxLength={100}
                    data-autofocus
                />
            </Field>

            <p className="mt-3 text-sm text-slate-500">
                {cart.lines.length} item(s), Rs. {money(totals.totalPayable)}
            </p>
        </Modal>
    );
};

/* ================================= Recall ================================= */

/** The list of held and open tickets. */
export const RecallDialog = ({ open, onClose, onRecall }) => {
    const [orders, setOrders] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        orderApi.listOpen()
            .then(setOrders)
            .catch((err) => toast.error(err.message))
            .finally(() => setLoading(false));
    }, [open]);

    return (
        <Modal open={open} onClose={onClose} title="Recall an order" width="lg">
            {loading ? (
                <SpinnerBlock label="Loading open orders…" />
            ) : !orders?.length ? (
                <EmptyState
                    icon={FiClock}
                    title="No open orders"
                    description="Held and unpaid tickets appear here."
                />
            ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                    {orders.map((order) => (
                        <button
                            key={order.Order_ID}
                            type="button"
                            onClick={() => onRecall(order.Order_ID)}
                            className="rounded-xl border border-slate-200 bg-white p-3 text-left transition-colors hover:border-brand-400 hover:bg-brand-50"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="truncate font-semibold text-slate-800">
                                        {order.Order_Number}
                                    </p>
                                    <p className="truncate text-sm text-slate-500">
                                        {order.Order_Type_Name}
                                        {order.Table_Number && ` · Table ${order.Table_Number}`}
                                    </p>
                                </div>
                                <StatusBadge status={order.Order_Status_Code} />
                            </div>

                            <div className="mt-2 flex items-end justify-between gap-2">
                                <div className="min-w-0 text-xs text-slate-500">
                                    <p className="truncate">{order.Customer_Name || 'Walk-in'}</p>
                                    <p>{order.Item_Count} item(s) · {relativeTime(order.Order_Date_Time)}</p>
                                </div>
                                <p className="tabular shrink-0 text-lg font-bold text-slate-800">
                                    {money(order.Total_Payable)}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </Modal>
    );
};

/* ================================ Discount ================================ */

/**
 * A discount on the whole bill.
 *
 * The named types come from DISCOUNT_TYPE, each with its own ceiling - a staff
 * discount that can write off the entire bill is how a POS loses money.
 */
export const DiscountDialog = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const cart = useSelector(selectCart);
    const totals = useSelector(selectTotals);
    const { discountTypes } = useSelector(selectLookups);

    const [typeId, setTypeId] = useState(null);
    const [mode, setMode] = useState('PERCENT');
    const [value, setValue] = useState('');

    useEffect(() => {
        if (!open) return;
        setTypeId(cart.discountTypeId);
        setMode(cart.discountAmount != null ? 'AMOUNT' : 'PERCENT');
        setValue(cart.discountAmount != null ? String(cart.discountAmount) : String(cart.discountPercent || ''));
    }, [open, cart.discountTypeId, cart.discountAmount, cart.discountPercent]);

    const selectedType = discountTypes.find((t) => t.Discount_Type_ID === typeId);
    const maxPercent = selectedType ? Number(selectedType.Max_Percent) : 100;
    const numeric = Number(value) || 0;

    const base = totals.grossAmount - totals.itemDiscount;
    const preview = mode === 'PERCENT'
        ? Math.round(base * Math.min(numeric, maxPercent)) / 100
        : Math.min(numeric, base);

    const exceedsCap = mode === 'PERCENT' && numeric > maxPercent;

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Discount"
            subtitle={`Bill before discount: Rs. ${money(base)}`}
            width="md"
            footer={(
                <>
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                            dispatch(setOrderDiscount({ discountTypeId: null, discountPercent: 0, discountAmount: null }));
                            onClose();
                        }}
                    >
                        Remove discount
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        disabled={exceedsCap}
                        onClick={() => {
                            dispatch(setOrderDiscount({
                                discountTypeId: typeId,
                                discountPercent: mode === 'PERCENT' ? numeric : 0,
                                discountAmount: mode === 'AMOUNT' ? numeric : null
                            }));
                            onClose();
                        }}
                    >
                        <FiCheck /> Apply
                    </button>
                </>
            )}
        >
            <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
                <div className="space-y-4">
                    <Field label="Reason">
                        <div className="grid grid-cols-2 gap-2">
                            {discountTypes.map((type) => (
                                <button
                                    key={type.Discount_Type_ID}
                                    type="button"
                                    onClick={() => {
                                        setTypeId(type.Discount_Type_ID);
                                        setMode(type.Calculation_Method);
                                        if (Number(type.Default_Value) > 0) setValue(String(type.Default_Value));
                                    }}
                                    className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors
                                                ${typeId === type.Discount_Type_ID
                                                    ? 'border-brand-600 bg-brand-50 ring-1 ring-brand-500'
                                                    : 'border-slate-300 hover:border-brand-400'}`}
                                >
                                    <span className="block font-medium text-slate-800">
                                        {type.Discount_Type_Name}
                                    </span>
                                    <span className="block text-xs text-slate-500">
                                        max {Number(type.Max_Percent)}%
                                        {type.Requires_Approval ? ' · manager' : ''}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </Field>

                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setMode('PERCENT')}
                            className={mode === 'PERCENT' ? 'btn-primary btn-sm flex-1' : 'btn-secondary btn-sm flex-1'}
                        >
                            <FiPercent className="h-3.5 w-3.5" /> Percent
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode('AMOUNT')}
                            className={mode === 'AMOUNT' ? 'btn-primary btn-sm flex-1' : 'btn-secondary btn-sm flex-1'}
                        >
                            Rs. Amount
                        </button>
                    </div>

                    <div className="rounded-lg bg-slate-50 p-3">
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Discount</span>
                            <span className="tabular font-semibold text-emerald-700">−{money(preview)}</span>
                        </div>
                        <div className="mt-1 flex justify-between text-sm">
                            <span className="text-slate-600">Bill after discount</span>
                            <span className="tabular font-semibold">{money(base - preview)}</span>
                        </div>
                        {exceedsCap && (
                            <p className="mt-2 text-sm text-red-600">
                                {selectedType?.Discount_Type_Name} allows at most {maxPercent}%.
                            </p>
                        )}
                    </div>
                </div>

                <div className="w-full sm:w-56">
                    <div className="mb-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-right">
                        <p className="text-xs text-slate-500">{mode === 'PERCENT' ? 'Percent' : 'Amount'}</p>
                        <p className="tabular text-2xl font-semibold">{value || '0'}</p>
                    </div>
                    <NumericKeypad value={value} onChange={setValue} maxDecimals={2} />
                </div>
            </div>
        </Modal>
    );
};

/* ================================== Note ================================== */

export const NoteDialog = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const cart = useSelector(selectCart);
    const [note, setNote] = useState('');

    useEffect(() => { if (open) setNote(cart.orderNote || ''); }, [open, cart.orderNote]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Order note"
            subtitle="Printed on the kitchen ticket."
            width="sm"
            footer={(
                <>
                    <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => { dispatch(setOrderNote(note.trim())); onClose(); }}
                    >
                        Save note
                    </button>
                </>
            )}
        >
            <textarea
                className="input"
                rows={4}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Birthday cake at the end, allergy: peanuts…"
                maxLength={1000}
                data-autofocus
            />
        </Modal>
    );
};

/* ================================ Customer ================================ */

export const CustomerDialog = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [draft, setDraft] = useState({ customerName: '', mobileNo: '', ntn: '', address: '' });

    // Debounced so a fast typist does not fire a request per keystroke.
    useEffect(() => {
        if (!open) return undefined;
        const timer = setTimeout(() => {
            setLoading(true);
            customerApi.search(search)
                .then(setResults)
                .catch(() => setResults([]))
                .finally(() => setLoading(false));
        }, 250);
        return () => clearTimeout(timer);
    }, [open, search]);

    const choose = (customer) => {
        dispatch(setCustomer({
            customerId: customer.Customer_ID,
            customerName: customer.Customer_Name,
            mobileNo: customer.Mobile_No
        }));
        onClose();
    };

    const create = async () => {
        if (!draft.customerName.trim()) {
            toast.error('Enter a name.');
            return;
        }
        try {
            const { customerId } = await customerApi.create(draft);
            dispatch(setCustomer({
                customerId,
                customerName: draft.customerName,
                mobileNo: draft.mobileNo
            }));
            toast.success(`${draft.customerName} added.`);
            onClose();
        } catch (err) {
            toast.error(err.message);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={creating ? 'New customer' : 'Choose a customer'}
            width="md"
            footer={creating ? (
                <>
                    <button type="button" className="btn-secondary" onClick={() => setCreating(false)}>
                        Back
                    </button>
                    <button type="button" className="btn-primary" onClick={create}>
                        Add customer
                    </button>
                </>
            ) : (
                <button type="button" className="btn-secondary" onClick={() => setCreating(true)}>
                    <FiUserPlus /> New customer
                </button>
            )}
        >
            {creating ? (
                <div className="space-y-3">
                    <Field label="Name" required>
                        <Input
                            value={draft.customerName}
                            onChange={(event) => setDraft({ ...draft, customerName: event.target.value })}
                            data-autofocus
                        />
                    </Field>
                    <Field label="Mobile">
                        <Input
                            value={draft.mobileNo}
                            onChange={(event) => setDraft({ ...draft, mobileNo: event.target.value })}
                            placeholder="0300-1234567"
                        />
                    </Field>
                    <Field
                        label="NTN / CNIC"
                        hint="Needed only if the customer wants a tax invoice in their name."
                    >
                        <Input
                            value={draft.ntn}
                            onChange={(event) => setDraft({ ...draft, ntn: event.target.value })}
                        />
                    </Field>
                    <Field label="Address">
                        <Input
                            value={draft.address}
                            onChange={(event) => setDraft({ ...draft, address: event.target.value })}
                        />
                    </Field>
                </div>
            ) : (
                <>
                    <div className="relative mb-3">
                        <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            className="input pl-9"
                            placeholder="Search by name, mobile or NTN"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            data-autofocus
                        />
                    </div>

                    {loading ? (
                        <SpinnerBlock />
                    ) : !results.length ? (
                        <EmptyState icon={FiUser} title="No matches" />
                    ) : (
                        <div className="space-y-1.5">
                            {results.map((customer) => (
                                <button
                                    key={customer.Customer_ID}
                                    type="button"
                                    onClick={() => choose(customer)}
                                    className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-left hover:border-brand-400 hover:bg-brand-50"
                                >
                                    <div className="min-w-0">
                                        <p className="truncate font-medium text-slate-800">
                                            {customer.Customer_Name}
                                            {customer.Is_Walk_In === true && (
                                                <span className="ml-2 text-xs font-normal text-slate-500">(default)</span>
                                            )}
                                        </p>
                                        <p className="truncate text-xs text-slate-500">
                                            {customer.Mobile_No || 'No mobile'}
                                            {customer.NTN && ` · NTN ${customer.NTN}`}
                                        </p>
                                    </div>
                                    {customer.Total_Orders > 0 && (
                                        <span className="tabular shrink-0 text-xs text-slate-500">
                                            {customer.Total_Orders} orders
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

/* ================================= Table ================================== */

export const TableDialog = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const [tables, setTables] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        tableApi.list()
            .then(setTables)
            .catch((err) => toast.error(err.message))
            .finally(() => setLoading(false));
    }, [open]);

    const areas = [...new Set((tables || []).map((t) => t.Area_Name || 'Main'))];

    return (
        <Modal open={open} onClose={onClose} title="Choose a table" width="lg">
            {loading ? (
                <SpinnerBlock />
            ) : !tables?.length ? (
                <EmptyState icon={FiGrid} title="No tables set up" description="Add them under Tables." />
            ) : (
                <div className="space-y-4">
                    {areas.map((area) => (
                        <section key={area}>
                            <h3 className="mb-2 text-sm font-semibold text-slate-600">{area}</h3>
                            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                                {tables
                                    .filter((table) => (table.Area_Name || 'Main') === area)
                                    .map((table) => {
                                        const isFree = table.Table_Status === 'FREE';

                                        return (
                                            <button
                                                key={table.Table_ID}
                                                type="button"
                                                onClick={() => {
                                                    dispatch(setTable({
                                                        tableId: table.Table_ID,
                                                        tableNumber: table.Table_Number
                                                    }));
                                                    onClose();
                                                }}
                                                className={`rounded-xl border-2 p-3 text-center transition-colors
                                                            ${isFree
                                                                ? 'border-emerald-300 bg-emerald-50 hover:border-emerald-500'
                                                                : 'border-blue-300 bg-blue-50 hover:border-blue-500'}`}
                                            >
                                                <p className="text-lg font-bold text-slate-800">
                                                    {table.Table_Number}
                                                </p>
                                                <p className="text-[11px] text-slate-500">
                                                    {table.Seating_Capacity} seats
                                                </p>
                                                {/* An occupied table shows its bill, so a waiter
                                                    can add to the right ticket rather than
                                                    opening a second one. */}
                                                {!isFree && (
                                                    <p className="tabular mt-0.5 text-[11px] font-medium text-blue-700">
                                                        {money(table.Total_Payable || 0)}
                                                        {table.Occupied_Minutes != null && (
                                                            <span className="block font-normal text-slate-500">
                                                                {duration(table.Occupied_Minutes)}
                                                            </span>
                                                        )}
                                                    </p>
                                                )}
                                            </button>
                                        );
                                    })}
                            </div>
                        </section>
                    ))}
                </div>
            )}
        </Modal>
    );
};

/* ============================== Line options ============================== */

/** Tapping a line on the bill: change its discount, its note, or remove it. */
export const LineOptionsDialog = ({ open, line, onClose }) => {
    const dispatch = useDispatch();
    const [discount, setDiscount] = useState('');
    const [note, setNote] = useState('');

    useEffect(() => {
        if (!open || !line) return;
        setDiscount(String(line.discountPercent || ''));
        setNote(line.itemNote || '');
    }, [open, line]);

    if (!line) return null;

    const numeric = Number(discount) || 0;
    const exceedsCap = numeric > (line.maxDiscountPercent ?? 100);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={line.itemName}
            subtitle={`${line.quantity} × Rs. ${money(line.unitRate)}`}
            width="sm"
            footer={(
                <>
                    <button
                        type="button"
                        className="btn-danger mr-auto"
                        onClick={() => { dispatch(removeLine(line.key)); onClose(); }}
                        disabled={line.isSentToKitchen}
                        title={line.isSentToKitchen ? 'Already sent to the kitchen' : undefined}
                    >
                        <FiTrash2 /> Remove
                    </button>
                    <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button
                        type="button"
                        className="btn-primary"
                        disabled={exceedsCap}
                        onClick={() => {
                            dispatch(setLineDiscount({ key: line.key, discountPercent: numeric }));
                            dispatch(setLineNote({ key: line.key, itemNote: note.trim() || null }));
                            onClose();
                        }}
                    >
                        Save
                    </button>
                </>
            )}
        >
            <div className="space-y-4">
                <Field
                    label="Line discount"
                    error={exceedsCap ? `This item allows at most ${line.maxDiscountPercent}%.` : null}
                >
                    <div className="flex items-center gap-2">
                        <Input
                            type="number"
                            className="tabular text-right"
                            value={discount}
                            onChange={(event) => setDiscount(event.target.value)}
                            min={0}
                            max={line.maxDiscountPercent ?? 100}
                        />
                        <span className="text-slate-500">%</span>
                    </div>
                </Field>

                <Field label="Note for the kitchen">
                    <Input
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        placeholder="no onions…"
                        maxLength={200}
                    />
                </Field>

                {line.isSentToKitchen && (
                    <p className="rounded-lg bg-blue-50 p-2.5 text-sm text-blue-800">
                        This line has already gone to the kitchen, so it cannot be removed here.
                        Void it from the Orders screen if the dish is not being made.
                    </p>
                )}
            </div>
        </Modal>
    );
};
